import React, { FC, useEffect, useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { useDialog } from "@natera/platform/lib/hooks";
import {
  OrderRenewalContext,
  RedirectionContext,
  TestCardContext,
  TestResultContext,
  UserContext,
} from "@app/provider";
import { useBillingAccess } from "@app/postBill/postBillWrapper";
import {
  SpinnerView,
  TestCard,
  SecondaryActionCard,
  FinancialAidCard,
  FlowHeader,
  BillingCenterUpdatesCard,
  BillingUpdatesCard,
} from "@app/components";
import { routes } from "@app/routing";
import { STAGE } from "@app/service/user";
import SetUpProfileDialog from "./setUpProfileDialog";
import "./home.scss";
import NoActiveTestsView from "./noActiveTestsView";
import classNames from "classnames";
import { useIsMobileDimension, useTokenizedLinks } from "@app/hooks";
import { RenewalBanner, EmpowerAlertBanner } from "@app/components/banners";
import {
  ParsedTestCard,
  SecondaryActionCardType,
  TestType,
} from "@app/provider/testData/types";

const messages = defineMessages({
  testCardAriaLabel: {
    id: "testCardAriaLabel",
    defaultMessage: "Test card",
  },
  homeWelcomeTitle: {
    id: "homeWelcomeTitle",
    defaultMessage: "Welcome to Natera’s Patient Portal",
  },
});

const Home: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();

  const [initializing, setInitializing] = useState(true);

  const { isLoading: isLoadingLink } = useTokenizedLinks();
  const { redirectionTrigger, handleRedirection } =
    useContext(RedirectionContext);

  useEffect(() => {
    if (redirectionTrigger) {
      handleRedirection();
    }
  }, [redirectionTrigger]);

  const {
    isLoading: isUppUserLoading,
    uppUser,
    loadProfile,
  } = useContext(UserContext);

  const {
    isLoading: isTestCardLoading,
    getTestsDataForHomePage,
    testCards,
    secondaryActionCards,
    isTestsHistoryExists,
    empowerRiskAlertInfo,
    empowerRiskAlertInfoLoading,
    getEmpowerRiskAlertInfo,
  } = useContext(TestCardContext);

  const {
    renewalInfo,
    getRenewalInfo,
    isLoading: isOrderRenewalLoading,
    openRenewalDialog,
  } = useContext(OrderRenewalContext);

  const {
    testResultInfoToken,
    getTestResultInfoFromToken,
    setTestResultInfoToken,
    isLoading: isTestResultLoading,
  } = useContext(TestResultContext);

  const { isLoading: isBillingAccessLoading } = useBillingAccess();

  const setUpProfileDialog = useDialog(SetUpProfileDialog);

  const hasSignateraOrder = (testCards?: ParsedTestCard[]): boolean => {
    if (!testCards) {
      return false;
    }
    return testCards.some(
      (testCard) => testCard.testType === TestType.SIGNATERA
    );
  };

  useEffect(() => {
    loadProfile().finally(() => setInitializing(false));
  }, []);

  useEffect(() => {
    if (hasSignateraOrder(testCards)) {
      getRenewalInfo({});
      getEmpowerRiskAlertInfo();
    }
  }, [testCards]);

  useEffect(() => {
    if (
      uppUser?.patientUID &&
      !isTestCardLoading &&
      uppUser?.stage === STAGE.FILLED
    ) {
      getTestsDataForHomePage();
    }
  }, [uppUser?.patientUID]);

  useEffect(() => {
    switch (uppUser?.stage) {
      case STAGE.BLANK:
      case STAGE.DATE_OF_BIRTH_VERIFICATION: // todo remove DATE_OF_BIRTH_VERIFICATION stage from db emums
      case STAGE.CONFIRMATION: {
        // todo remove CONFIRMATION stage from db emums
        setUpProfileDialog.open({
          isLoading: isUppUserLoading,
          onSubmit: () => {
            if (uppUser.invite) {
              navigate(routes.confirmation + location.search, {
                replace: true,
              });
            } else {
              navigate(routes.setupProfile + location.search, {
                replace: true,
              });
            }
          },
        });

        break;
      }

      case STAGE.PROFILE_SETUP: {
        setUpProfileDialog.open({
          isLoading: isUppUserLoading,
          onSubmit: () => {
            navigate(routes.setupProfile + location.search, {
              replace: true,
            });
          },
        });

        break;
      }

      case STAGE.MFA_SETUP:
      case STAGE.COMMUNICATION_PREFERENCES: {
        if (uppUser.invite) {
          navigate(routes.confirmation + location.search, { replace: true });
        } else {
          navigate(routes.setupProfile, { replace: true });
        }
        break;
      }

      case STAGE.FILLED: {
        if (testResultInfoToken) {
          getTestResultInfoFromToken(testResultInfoToken)
            .then((info) => {
              if (info) {
                navigate(
                  routes.testResultDetailsPage(
                    info.orderUidPublic,
                    info.testUid
                  ),
                  { replace: true }
                );
              }
            })
            .finally(() => {
              setTestResultInfoToken("");
            });
        }

        break;
      }
    }
  }, [uppUser, isUppUserLoading]);

  const isMobile = useIsMobileDimension();
  const renderTestCards = () => (
    <div className="home__test-cards-column">
      {renewalInfo?.hasRenewal && isMobile && (
        <RenewalBanner
          openRenewalDialog={openRenewalDialog}
          limsId={renewalInfo?.clinicLimsId}
          orderUidPublic={renewalInfo.renewalOrderUidPublic}
        />
      )}
      {testCards?.map((testCard) => (
        <div
          key={testCard.uid}
          role="group"
          aria-label={intl.formatMessage(messages.testCardAriaLabel)}
        >
          <TestCard testCard={testCard} leftSideClickable />
        </div>
      ))}
    </div>
  );

  const renderSecondaryActionCards = () => {
    return (
      <div className="home__test-cards-column">
        {empowerRiskAlertInfo?.isEligible && (
          <EmpowerAlertBanner limsId={empowerRiskAlertInfo?.clinicLimsId} />
        )}
        {renewalInfo?.hasRenewal && !isMobile && (
          <RenewalBanner
            openRenewalDialog={openRenewalDialog}
            limsId={renewalInfo?.clinicLimsId}
            orderUidPublic={renewalInfo.renewalOrderUidPublic}
          />
        )}
        {secondaryActionCards.map((card) =>
          card.type === SecondaryActionCardType.COMPASSIONATE_CARE ? (
            <FinancialAidCard
              key={card.type}
              limsClinicId={card.limsClinicId}
              title={card.title!}
              description={card.description!}
              note={card.note}
              showIcon
              showButton
            />
          ) : (
            <SecondaryActionCard key={card.type} card={card} />
          )
        )}
      </div>
    );
  };

  const isLoading =
    initializing ||
    isUppUserLoading ||
    isTestCardLoading ||
    isTestResultLoading ||
    isLoadingLink ||
    isOrderRenewalLoading ||
    empowerRiskAlertInfoLoading ||
    isBillingAccessLoading;

  const noActiveTestsView =
    uppUser?.stage === STAGE.FILLED &&
    testCards &&
    testCards.length === 0 &&
    !isLoading;

  return (
    <section
      className={classNames("home__container", {
        no_active_tests_view: noActiveTestsView,
      })}
      data-testid="home-page"
    >
      <SpinnerView isLoading={isLoading} />
      {uppUser?.stage !== STAGE.FILLED && <FlowHeader />}
      {uppUser?.stage === STAGE.FILLED && !noActiveTestsView && (
        <h1 className="home__welcome-title">
          {intl.formatMessage(messages.homeWelcomeTitle)}
        </h1>
      )}
      {setUpProfileDialog.getDialog()}
      <BillingUpdatesCard />
      <BillingCenterUpdatesCard />
      <div className="home__test-cards">
        {testCards && renderTestCards()}
        {renderSecondaryActionCards()}
      </div>
      {noActiveTestsView && (
        <NoActiveTestsView isTestsHistoryExists={isTestsHistoryExists} />
      )}
    </section>
  );
};

export default Home;
